import { z } from "zod";

/**
 * The hard ceiling on any list endpoint, shared by the API's validator and the
 * frontend's URL search-param schema.
 *
 * Living in one place matters: WSL's "1000-row query" fires all traced back to
 * a list route whose cap existed on one side only, so a hand-edited URL or a
 * forgotten default meant "all of them".
 */
export const PAGE_SIZE_MAX = 100;
export const PAGE_SIZE_DEFAULT = 25;

/**
 * Offset pagination. Acceptable for small admin lookups (sub-agencies, sports)
 * where the total genuinely is small and users want page numbers.
 *
 * Keys stay snake_case here — the API reads them off the query string, and the
 * frontend puts them in the URL, so both sides speak the wire format.
 */
export const paginationQuery = z.object({
	page: z.coerce.number().int().min(1).default(1),
	page_size: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});

export type PaginationQuery = z.infer<typeof paginationQuery>;

/**
 * The free-text `search` param every list endpoint takes. Trimmed BEFORE the
 * length check: zod 3 runs checks in order, so `.min(1).trim()` lets "   "
 * through as "" (the copy-pasted bug this replaces).
 */
export const searchParam = z.string().trim().min(1).max(100);

/**
 * Keyset pagination. Prefer this for anything time-ordered: `OFFSET 50000`
 * makes Postgres walk and discard 50,000 rows on every request; a cursor does
 * not.
 */
export const keysetQuery = z.object({
	cursor: z.string().min(1).max(200).optional(),
	limit: z.coerce.number().int().min(1).max(PAGE_SIZE_MAX).default(PAGE_SIZE_DEFAULT),
});

export type KeysetQuery = z.infer<typeof keysetQuery>;
